import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  MenuItem,
  Autocomplete,
  Grid,
  Collapse,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  CheckCircle,
  Cancel as CancelIcon,
  Add,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  Cancel,
  Save,
  Delete,
  InfoOutlined,
} from '@mui/icons-material';
import { FiX, FiAlertTriangle, FiMail } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { campaignsAPI, emailTemplatesAPI, whatsappTemplatesAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import WhatsAppTemplatePreview from '../components/WhatsAppTemplatePreview';
import { getSortIcon } from '../utils/sortIcon';
import { colors } from '../theme/modernTheme';
import TagAutocomplete from '../components/TagAutocomplete';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { truncateText } from '../utils/truncate';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTags, selectGlobalTags, selectGlobalTagsStatus } from '../store/slices/tagsSlice';
import { COMMUNICATION_CHANNEL } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import { MODULE_PERMISSIONS } from '../utils/modulePermissions';
import { getWhatsAppTemplateTypeIcon } from '../components/WhatsAppTemplateTypeIcon';
import { usePermissionNavigation } from '../hooks/usePermissionNavigation';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const navigateWithPermission = usePermissionNavigation();
  const location = useLocation();
  const { hasModulePermission } = useAuth();

  // Channel permissions based on user's module permissions
  const canUseEmail = hasModulePermission(MODULE_PERMISSIONS.EMAIL_MARKETING);
  const canUseWhatsApp = hasModulePermission(MODULE_PERMISSIONS.WHATSAPP_MARKETING);

  const emailTemplatePermission = hasModulePermission(MODULE_PERMISSIONS.EMAIL_TEMPLATE);

  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [whatsappTemplate, setWhatsappTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPerPage] = useState(50);
  const [contactsPagination, setContactsPagination] = useState({
    total: 0,
    pages: 0,
  });

  // Contact Events state - kept for filtering contacts by event type
  const [eventFilter, setEventFilter] = useState('');
  const [appliedEventFilter, setAppliedEventFilter] = useState('');

  const [emailSectionOpen, setEmailSectionOpen] = useState(false);
  const [whatsappSectionOpen, setWhatsappSectionOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'DRAFT',
  });
  const [formErrors, setFormErrors] = useState({});

  // Communication channel - single selection
  const [selectedChannel, setSelectedChannel] = useState(COMMUNICATION_CHANNEL.EMAIL);

  // Email template selection
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // WhatsApp template selection
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState(null);
  const [loadingWhatsappTemplates, setLoadingWhatsappTemplates] = useState(false);

  // Tags selection
  const dispatch = useDispatch();
  const availableTags = useSelector(selectGlobalTags);
  const tagsStatus = useSelector(selectGlobalTagsStatus);
  const loadingTags = tagsStatus === 'loading';
  const [selectedTags, setSelectedTags] = useState([]);

  // Enrollment state
  const [enrollmentType, setEnrollmentType] = useState('ALL'); // 'ALL', 'TAGS' or 'MANUAL'
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [loadingAllContacts, setLoadingAllContacts] = useState(false);
  const [enrollmentPage, setEnrollmentPage] = useState(1);
  const [enrollmentItemsPerPage] = useState(50);
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [confirmActivateOpen, setConfirmActivateOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToUnenroll, setContactToUnenroll] = useState(null);
  const [unenrolling, setUnenrolling] = useState(false);

  // WhatsApp retry state
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [failedStats, setFailedStats] = useState([]);
  const [selectedErrorGroups, setSelectedErrorGroups] = useState([]);
  const [loadingFailedStats, setLoadingFailedStats] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Set default channel based on user permissions
  useEffect(() => {
    if (id === 'new') {
      // For new campaigns, set default channel based on permissions
      if (canUseEmail) {
        setSelectedChannel(COMMUNICATION_CHANNEL.EMAIL);
      } else if (canUseWhatsApp) {
        setSelectedChannel(COMMUNICATION_CHANNEL.WHATSAPP);
      }
    }
  }, [id, canUseEmail, canUseWhatsApp]);

  useEffect(() => {
    const initializeData = async () => {
      if (id === 'new') {
        // For new campaigns, only load templates
        setLoading(false);
        setIsEditMode(true);
        if (canUseEmail) await loadEmailTemplates();
        if (canUseWhatsApp) await loadWhatsappTemplates();
        if (tagsStatus === 'idle') dispatch(fetchTags({ limit: 10000 }));
      } else {
        // For existing campaigns, we just need to load campaign data
        await loadCampaign();
        if (tagsStatus === 'idle') dispatch(fetchTags({ limit: 10000 }));
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle return from email template editor and edit mode
  useEffect(() => {
    if (location.state?.selectedTemplateId) {
      const templateId = location.state.selectedTemplateId;
      loadEmailTemplates().then(() => {
        const template = emailTemplates.find((t) => t.id === parseInt(templateId));
        if (template) {
          setSelectedTemplate(template);
        }
      });

      // Restore campaign data if provided
      if (location.state.campaignData) {
        setFormData(location.state.campaignData);
      }

      // Clear the state
      window.history.replaceState({}, document.title);
    }

    // Handle edit mode from navigation
    if (location.state?.editMode && campaign && id !== 'new') {
      handleEditMode();
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }

    // Handle view mode from navigation (after creating new campaign)
    if (location.state?.viewMode && campaign && id !== 'new') {
      setIsEditMode(false);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }

    // Check for edit query parameter in URL
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true' && campaign && id !== 'new') {
      handleEditMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, campaign]);

  useEffect(() => {
    if (activeTab === 1 && contacts.length === 0) {
      loadContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Reload contacts when page, sort, search, or event filter changes
  useEffect(() => {
    if (activeTab === 1 && id !== 'new') {
      loadContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsPage, sortConfig, appliedSearchTerm, appliedEventFilter]);

  // Note: Removed auto-reload on search term change
  // Contacts now reload only when Apply button is clicked or Enter is pressed

  // Load email/whatsapp template when campaign is loaded
  useEffect(() => {
    if (campaign && campaign.email_template_id && id !== 'new') {
      loadEmailTemplate();
    }

    if (campaign && campaign.whatsapp_template_id && id !== 'new') {
      loadWhatsappTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  // Set selected template when both campaign and templates are loaded
  useEffect(() => {
    if (campaign && campaign.email_template_id && emailTemplates.length > 0) {
      const template = emailTemplates.find((t) => t.id === campaign.email_template_id);
      if (template) {
        setSelectedTemplate(template);
        setEmailTemplate(template); // Also set emailTemplate for view mode display
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, emailTemplates]);

  // Set selected WhatsApp template when both campaign and whatsappTemplates are loaded
  useEffect(() => {
    if (campaign && campaign.whatsapp_template_id && whatsappTemplates.length > 0) {
      const template = whatsappTemplates.find((t) => t.id === campaign.whatsapp_template_id);
      if (template) {
        setSelectedWhatsappTemplate(template);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, whatsappTemplates]);

  const loadCampaign = async () => {
    if (id === 'new') {
      // New campaign mode - don't try to load data
      setLoading(false);
      setCampaign(null);
      setIsEditMode(true);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const campaignResponse = await campaignsAPI.getOne(id);
      const campaignData = campaignResponse.data;
      setCampaign(campaignData);

      setFormData({
        name: campaignData.name || '',
        description: campaignData.description || '',
        status: campaignData.status || 'DRAFT',
        communication_channel: campaignData.communication_channel || COMMUNICATION_CHANNEL.EMAIL,
      });

      // Set selected tags from campaign data
      setSelectedTags(campaignData.tags || []);

      // Set communication channel from campaign data
      if (campaignData.communication_channel) {
        setSelectedChannel(campaignData.communication_channel);
      }

      // Set selected template from campaign data (templates should already be loaded)
      if (campaignData.email_template_id && emailTemplates.length > 0) {
        const template = emailTemplates.find((t) => t.id === campaignData.email_template_id);
        if (template) {
          setSelectedTemplate(template);
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.status === 404 ? 'Campaign not found' : 'Failed to load campaign details';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await emailTemplatesAPI.getAll({ limit: 100 });
      setEmailTemplates(response.data || []);
      return response.data || [];
    } catch (error) {
      showError('Failed to load email templates');
      return [];
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadWhatsappTemplates = async () => {
    setLoadingWhatsappTemplates(true);
    try {
      // Load only approved templates
      const response = await whatsappTemplatesAPI.getAll({
        limit: 100,
        status: 'APPROVED',
      });

      const { templates = [] } = response.data || {};

      setWhatsappTemplates(templates);
      return [];
    } catch (error) {
      showError('Failed to load WhatsApp templates');
      return [];
    } finally {
      setLoadingWhatsappTemplates(false);
    }
  };

  const loadAllContacts = async () => {
    setLoadingAllContacts(true);
    try {
      // Determine campaign type
      const isEmailCampaign =
        campaign?.email_template_id !== null && campaign?.email_template_id !== undefined;
      const isWhatsAppCampaign =
        campaign?.whatsapp_template_id !== null && campaign?.whatsapp_template_id !== undefined;

      // Build query parameters based on campaign type
      const params = { minimal: true };

      // For email-only campaigns, filter contacts with email on backend
      if (isEmailCampaign && !isWhatsAppCampaign) {
        params.has_email = true;
      }
      // For WhatsApp-only campaigns, filter contacts with phone on backend
      else if (isWhatsAppCampaign && !isEmailCampaign) {
        params.has_phone = true;
      }
      // For mixed campaigns, we don't add backend filters
      // We'll filter on frontend to show contacts with either email or phone

      const response = await campaignsAPI.getAllContacts(params);
      let allContactsList = response.data || [];

      // For mixed campaigns, filter to show only contacts with either email or phone
      if (isEmailCampaign && isWhatsAppCampaign) {
        allContactsList = allContactsList.filter(
          (c) =>
            (c.primary_email && c.primary_email.trim() !== '') ||
            (c.phone_1 && c.phone_1.trim() !== ''),
        );
      }

      // Filter out contacts that are already enrolled in this campaign
      const enrolledContactIds = new Set(contacts.map((c) => c.id));
      const unenrolledContacts = allContactsList.filter((c) => !enrolledContactIds.has(c.id));

      setAllContacts(unenrolledContacts);
    } catch (error) {
      showError('Failed to load contacts');
    } finally {
      setLoadingAllContacts(false);
    }
  };

  const handleEnrollContacts = async () => {
    if (enrollmentType === 'TAGS' && selectedTags.length === 0) {
      showError('Please select at least one tag to enroll contacts.');
      return;
    }

    if (enrollmentType === 'MANUAL' && selectedContactIds.length === 0) {
      showError('Please select at least one contact to enroll.');
      return;
    }

    try {
      setLoading(true);

      const criteria = {
        type: enrollmentType,
        tagIds: enrollmentType === 'TAGS' ? selectedTags.map((t) => t.id) : [],
        contactIds: enrollmentType === 'MANUAL' ? selectedContactIds : [],
      };

      const response = await campaignsAPI.enrollContacts(id, criteria);
      const enrolledCount = response.data?.contactsEnrolled || response.contactsEnrolled || 0;

      showSuccess(`Successfully enrolled ${enrolledCount} contact(s).`);

      // Reload contacts and campaign
      await loadContacts();
      await loadCampaign();

      // Then close modal and reset parameters
      setEnrollModalOpen(false);
      setSelectedTags([]);
      setSelectedContactIds([]);
      setEnrollmentPage(1);

      setActiveTab(1); // Stay on Campaign Contacts tab
    } catch (error) {
      console.error('Error enrolling contacts:', error);
      showError(
        error.response?.data?.error?.message || error.message || 'Failed to enroll contacts',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCampaign = () => {
    if (!campaign || campaign.total_contacts === 0) {
      showError('Please enroll at least one contact before activating the campaign.');
      return;
    }

    // TODO: check do we really need these checks
    if (selectedChannel === COMMUNICATION_CHANNEL.EMAIL && !campaign?.email_template_id) {
      showError('Please select an email template before activating the campaign.');
      return;
    }

    if (selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP && !campaign?.whatsapp_template_id) {
      showError('Please select a WhatsApp template before activating the campaign.');
      return;
    }

    setConfirmActivateOpen(true);
  };

  const handleConfirmActivate = async () => {
    try {
      setLoading(true);
      setConfirmActivateOpen(false);
      await campaignsAPI.update(id, {
        ...formData,
        status: 'ACTIVE',
        communication_channel: selectedChannel,
        email_template_id:
          selectedChannel === COMMUNICATION_CHANNEL.EMAIL
            ? selectedTemplate?.id || campaign?.email_template_id || null
            : null,
        whatsapp_template_id:
          selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP
            ? selectedWhatsappTemplate?.id || campaign?.whatsapp_template_id || null
            : null,
      });
      showSuccess('Campaign activated successfully!');
      await loadCampaign();
    } catch (error) {
      console.error('Error activating campaign:', error);
      showError(
        error.response?.data?.error?.message || error.message || 'Failed to activate campaign',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUnenroll = async () => {
    if (!contactToUnenroll) return;

    try {
      setUnenrolling(true);
      await campaignsAPI.unenrollContact(id, contactToUnenroll.id);
      showSuccess('Contact removed from campaign');
      setDeleteConfirmOpen(false);
      setContactToUnenroll(null);
      await loadContacts();
      await loadCampaign();
    } catch (error) {
      console.error('Error removing contact:', error);
      showError('Failed to remove contact');
    } finally {
      setUnenrolling(false);
    }
  };

  const handleOpenRetryModal = async () => {
    try {
      setLoadingFailedStats(true);
      setRetryModalOpen(true);
      const response = await campaignsAPI.getWhatsAppFailedStats(id);
      setFailedStats(response.data || []);
      setSelectedErrorGroups([]);
    } catch (error) {
      console.error('Error loading failed stats:', error);
      showError('Failed to load failed message statistics');
      setRetryModalOpen(false);
    } finally {
      setLoadingFailedStats(false);
    }
  };

  const handleToggleErrorGroup = (errorReason) => {
    setSelectedErrorGroups((prev) => {
      if (prev.includes(errorReason)) {
        return prev.filter((e) => e !== errorReason);
      } else {
        return [...prev, errorReason];
      }
    });
  };

  const handleRetryMessages = async () => {
    if (selectedErrorGroups.length === 0) {
      showError('Please select at least one error group to retry');
      return;
    }

    try {
      setRetrying(true);

      // Collect all contact IDs from selected error groups
      const contactIds = failedStats
        .filter((stat) => selectedErrorGroups.includes(stat.error_reason))
        .flatMap((stat) => stat.contact_ids);

      await campaignsAPI.retryWhatsAppMessages(id, { contact_ids: contactIds });

      showSuccess(`Campaign reactivated. ${contactIds.length} contacts queued for retry.`);
      setRetryModalOpen(false);
      setSelectedErrorGroups([]);

      // Reload campaign to reflect new status
      await loadCampaign();
    } catch (error) {
      console.error('Error retrying messages:', error);
      showError(error?.message || 'Failed to retry messages');
    } finally {
      setRetrying(false);
    }
  };

  const handleCreateEmail = () => {
    navigate('/email-templates/new', {
      state: {
        returnTo: id === 'new' ? '/campaigns/new' : `/campaigns/${id}`,
        campaignData: formData,
        fromCampaign: true,
        selectedTags: selectedTags,
      },
    });
  };

  const handleSave = async () => {
    // Clear previous errors
    setFormErrors({});

    // Frontend validation
    const errors = {};

    // 1. Campaign Name validation
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Campaign name is required';
    } else if (formData.name.length > 100) {
      errors.name = 'Campaign name must not exceed 100 characters';
    }

    // 2. Status validation
    if (formData.status && !['DRAFT', 'ACTIVE'].includes(formData.status)) {
      errors.status = 'Status must be either DRAFT or ACTIVE';
    }

    // 3. Email Template validation
    if (selectedChannel === COMMUNICATION_CHANNEL.EMAIL && !selectedTemplate?.id) {
      errors.email_template =
        'Email template is required when Email channel is selected. Please select an existing template or create a new one.';
    }

    // 4. WhatsApp Template validation (only if whatsapp channel is selected)
    if (selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP && !selectedWhatsappTemplate?.id) {
      errors.whatsapp_template =
        'WhatsApp template is required when WhatsApp channel is selected. Please select an existing template or create a new one.';
    }

    // If there are validation errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Don't show toast - field errors are displayed below each field
      return;
    }

    try {
      const campaignData = {
        ...formData,
        status: id === 'new' ? 'DRAFT' : formData.status, // Force DRAFT for new campaigns
        communication_channel: selectedChannel,
        email_template_id:
          selectedChannel === COMMUNICATION_CHANNEL.EMAIL ? selectedTemplate?.id || null : null,
        whatsapp_template_id:
          selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP
            ? selectedWhatsappTemplate?.id || null
            : null,
      };

      if (id === 'new') {
        const response = await campaignsAPI.create(campaignData);
        const campaignId = response.data.id;

        // Tags are now assigned during creation, no need to assign separately
        // This ensures tags are assigned BEFORE contacts are seeded for ACTIVE campaigns

        showSuccess('Campaign created successfully');
        // Navigate to view mode (not edit mode)
        navigate(`/campaigns/${campaignId}`, {
          replace: true,
          state: { viewMode: true },
        });
      } else {
        await campaignsAPI.update(id, campaignData);

        // Update tags - compare current tags with selected tags
        const currentTagIds = campaign.tags?.map((t) => t.id) || [];
        const selectedTagIds = selectedTags.map((t) => t.id);

        // Remove tags that are no longer selected
        for (const tagId of currentTagIds) {
          if (!selectedTagIds.includes(tagId)) {
            try {
              await campaignsAPI.removeTag(id, tagId);
            } catch (error) {}
          }
        }

        // Add new tags
        for (const tag of selectedTags) {
          if (!currentTagIds.includes(tag.id)) {
            try {
              await campaignsAPI.assignTag(id, tag.id);
            } catch (error) {}
          }
        }

        showSuccess('Campaign updated successfully');
        // Navigate to view mode (without editMode state)
        navigate(`/campaigns/${id}`, { replace: true });
        // Reload campaign data
        await loadCampaign();
        // Reload contacts if on Campaign Contacts tab (for DRAFT campaigns with tag changes)
        if (activeTab === 1) {
          await loadContacts();
        }
        setIsEditMode(false);
      }
    } catch (error) {
      // The API interceptor already unwraps error.response.data, so error is the data object
      if (error.error?.code === 'VALIDATION_ERROR') {
        const fieldErrors = error.error.details?.field_errors || {};
        setFormErrors(fieldErrors);
        // Don't show toast - field errors are displayed below each field
      } else if (error.error?.message?.includes('already exists')) {
        // Handle unique name constraint
        setFormErrors({ name: error.error.message });
        // Don't show toast - error is displayed below name field
      } else {
        showError(error.error?.message || error.message || 'Failed to save campaign');
      }
    }
  };

  const handleEditMode = async () => {
    // Set communication channel based on existing communication_channel
    if (campaign.communication_channel) {
      setSelectedChannel(campaign.communication_channel);
    }

    // Load templates if not already loaded
    if (
      campaign.communication_channel === COMMUNICATION_CHANNEL.EMAIL &&
      emailTemplates.length === 0 &&
      canUseEmail
    ) {
      await loadEmailTemplates();
    }
    if (
      campaign.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP &&
      whatsappTemplates.length === 0 &&
      canUseWhatsApp
    ) {
      await loadWhatsappTemplates();
    }

    // Set form data
    setFormData({
      name: campaign.name || '',
      description: campaign.description || '',
      status: campaign.status || 'DRAFT',
    });
    setSelectedTags(campaign.tags || []);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setFormErrors({});
    if (id === 'new') {
      navigate('/campaigns');
    } else {
      setIsEditMode(false);
      setFormData({
        name: campaign.name || '',
        description: campaign.description || '',
        status: campaign.status || 'DRAFT',
      });
      setSelectedTags(campaign.tags || []);
    }
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const params = {
        sortBy: sortConfig.key || 'added_at',
        sortOrder: sortConfig.direction || 'desc',
        page: contactsPage,
        limit: contactsPerPage,
      };

      // Add search parameter if search term is applied
      if (appliedSearchTerm && appliedSearchTerm.trim() !== '') {
        params.search = appliedSearchTerm;
      }

      // Add event filter parameter if event filter is applied
      if (appliedEventFilter && appliedEventFilter.trim() !== '') {
        params.eventFilter = appliedEventFilter;
      }

      const response = await campaignsAPI.getContacts(id, params);
      setContacts(response.data || []);
      // Store pagination metadata
      if (response.pagination) {
        setContactsPagination({
          total: response.pagination.total,
          pages: response.pagination.pages,
        });
      }
    } catch (error) {
      showError('Failed to load campaign contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const loadEmailTemplate = async () => {
    setEmailLoading(true);
    try {
      // Load the campaign to get the email_template_id
      if (campaign && campaign.email_template_id) {
        const response = await emailTemplatesAPI.getById(campaign.email_template_id);

        setEmailTemplate(response.data);
      } else {
        setEmailTemplate(null);
      }
    } catch (error) {
      showError('Failed to load email template');
    } finally {
      setEmailLoading(false);
    }
  };

  const loadWhatsappTemplate = async () => {
    setWhatsappLoading(true);
    try {
      // Load the campaign to get the whatsapp_template_id
      if (campaign && campaign.whatsapp_template_id) {
        const response = await whatsappTemplatesAPI.getById(campaign.whatsapp_template_id);

        const { template } = response.data;

        setWhatsappTemplate(template);
      } else {
        setWhatsappTemplate(null);
      }
    } catch (error) {
      showError('Failed to load WhatsApp template');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Reload contacts with new sort order
    loadContacts();
  };

  const handleApplyFilters = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedEventFilter(eventFilter);
    setContactsPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEventFilter('');
    setAppliedSearchTerm('');
    setAppliedEventFilter('');
    setContactsPage(1);
  };

  // Check if filters have changed from applied state
  const hasFilterChanges = searchTerm !== appliedSearchTerm || eventFilter !== appliedEventFilter;

  // Calculate total contacts to retry from selected error groups
  const contactsToRetryCount =
    selectedErrorGroups.length > 0
      ? failedStats
          .filter((s) => selectedErrorGroups.includes(s.error_reason))
          .reduce((sum, s) => sum + s.count, 0)
      : 0;

  const getSortedAndFilteredContacts = () => {
    // Backend handles search, sorting, and event filtering
    return [...contacts];
  };

  const getPaginatedContacts = () => {
    // Backend handles pagination, just return filtered contacts
    return getSortedAndFilteredContacts();
  };

  const getTotalPages = () => {
    // Use backend pagination metadata
    return contactsPagination.pages || 1;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // if (error && !campaign && id !== "new") {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Button
  //         onClick={() => navigate("/campaigns")}
  //         startIcon={<ArrowBack />}>
  //         Back to Campaigns
  //       </Button>
  //     </Box>
  //   );
  // }

  // Show form for new campaign or edit mode
  if (id === 'new' || isEditMode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton
              onClick={() => navigate('/campaigns')}
              size="small"
              sx={{
                '&:hover': { backgroundColor: colors.action.hover },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                fontSize: '1.2rem',
              }}
            >
              {id === 'new' ? 'Create New Campaign' : 'Edit Campaign'}
            </Typography>
          </Box>
          <Box display="flex" gap={1.5}>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<Save fontSize="small" />}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                backgroundColor: colors.primary.main,
                '&:hover': {
                  backgroundColor: colors.primary.dark,
                },
              }}
            >
              {id === 'new' ? 'Create Draft Campaign' : 'Save Campaign'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Campaign Details - Full Width */}
          <Grid item xs={12}>
            <Card
              sx={{
                p: 2,
                borderRadius: '4px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: `1px solid ${colors.border.main}`,
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  pb: 2,
                  fontSize: '1rem',
                  textTransform: 'capitalize',
                  borderBottom: `1px solid ${colors.border.main}`,
                  color: colors.text.primary,
                }}
              >
                Campaign Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <TextField
                    fullWidth
                    size="meidum"
                    label="Campaign Name"
                    value={formData.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setFormData({ ...formData, name: value });
                      }
                      if (formErrors.name) {
                        setFormErrors({ ...formErrors, name: undefined });
                      }
                    }}
                    required
                    error={Boolean(formErrors.name)}
                    helperText={formErrors.name}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: '0.75rem' },
                    }}
                    InputLabelProps={{
                      style: { fontSize: '0.875rem' },
                    }}
                    inputProps={{
                      style: { fontSize: '0.875rem' },
                      maxLength: 100,
                    }}
                  />
                  {!formErrors.name && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formData.name.length} / 100
                    </Typography>
                  )}
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={formData.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 500) {
                        setFormData({ ...formData, description: value });
                      }
                      if (formErrors.description) {
                        setFormErrors({
                          ...formErrors,
                          description: undefined,
                        });
                      }
                    }}
                    multiline
                    rows={3}
                    error={Boolean(formErrors.description)}
                    helperText={formErrors.description}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    InputLabelProps={{
                      style: { fontSize: '0.875rem' },
                    }}
                    inputProps={{
                      style: { fontSize: '0.875rem' },
                      maxLength: 500,
                    }}
                  />
                  {!formErrors.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formData.description.length} / 500
                    </Typography>
                  )}
                </Box>
                {/* Status field is hidden - campaigns are created as DRAFT and can be activated via the Activate button */}

                {/* Communication Channels Section - Only show for new campaigns */}
                {id === 'new' && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        color: formErrors.communication_channel
                          ? colors.error.main
                          : colors.text.primary,
                      }}
                    >
                      Communication Channel <span style={{ color: colors.error.main }}>*</span>
                    </Typography>
                    {formErrors.communication_channel && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.error.main,
                          display: 'block',
                          mb: 1,
                          fontSize: '0.75rem',
                        }}
                      >
                        {formErrors.communication_channel}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {/* Email Channel - Only show if user has email marketing permission */}
                      {canUseEmail && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: `1px solid ${
                              selectedChannel === COMMUNICATION_CHANNEL.EMAIL
                                ? colors.primary.main
                                : colors.border.main
                            }`,
                            borderRadius: '4px',
                            p: 1,
                            cursor: 'pointer',
                            backgroundColor:
                              selectedChannel === COMMUNICATION_CHANNEL.EMAIL
                                ? `${colors.primary.main}08`
                                : 'transparent',
                            '&:hover': {
                              borderColor: colors.primary.main,
                              backgroundColor: `${colors.primary.main}08`,
                            },
                          }}
                          onClick={() => {
                            setFormErrors({
                              ...formErrors,
                              communication_channel: '',
                              email_template: '',
                            });
                            setSelectedChannel(COMMUNICATION_CHANNEL.EMAIL);
                          }}
                        >
                          <input
                            type="radio"
                            checked={selectedChannel === COMMUNICATION_CHANNEL.EMAIL}
                            onChange={() => {}}
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                          />
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            Email
                          </Typography>
                        </Box>
                      )}
                      {/* WhatsApp Channel - Only show if user has whatsapp marketing permission */}
                      {canUseWhatsApp && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: `1px solid ${
                              selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP
                                ? colors.primary.main
                                : colors.border.main
                            }`,
                            borderRadius: '4px',
                            p: 1,
                            cursor: 'pointer',
                            backgroundColor:
                              selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP
                                ? `${colors.primary.main}08`
                                : 'transparent',
                            '&:hover': {
                              borderColor: colors.primary.main,
                              backgroundColor: `${colors.primary.main}08`,
                            },
                          }}
                          onClick={() => {
                            setFormErrors({
                              ...formErrors,
                              communication_channel: '',
                              whatsapp_template: '',
                            });
                            setSelectedChannel(COMMUNICATION_CHANNEL.WHATSAPP);
                          }}
                        >
                          <input
                            type="radio"
                            checked={selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP}
                            onChange={() => {}}
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                          />
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            WhatsApp
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {/* Show message if user has no channel permissions */}
                    {!canUseEmail && !canUseWhatsApp && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        You don't have permission to use any communication channels. Please contact
                        your administrator.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Email Template Selection - Show only when email channel is selected */}
                {selectedChannel === COMMUNICATION_CHANNEL.EMAIL && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        color: formErrors.email_template ? colors.error.main : colors.text.primary,
                      }}
                    >
                      Email Template <span style={{ color: colors.error.main }}>*</span>
                    </Typography>
                    {formErrors.email_template && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.error.main,
                          display: 'block',
                          mb: 1,
                          fontSize: '0.75rem',
                        }}
                      >
                        {formErrors.email_template}
                      </Typography>
                    )}
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <Autocomplete
                          size="small"
                          options={emailTemplates}
                          getOptionLabel={(option) => option.name}
                          value={selectedTemplate}
                          onChange={(event, newValue) => setSelectedTemplate(newValue)}
                          loading={loadingTemplates}
                          openOnFocus
                          ListboxProps={{
                            style: {
                              maxHeight: '250px',
                            },
                          }}
                          slotProps={{
                            paper: {
                              sx: {
                                borderRadius: '4px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                '& .MuiAutocomplete-option': {
                                  py: 1.5,
                                  backgroundColor: 'transparent !important',
                                  '&:hover': {
                                    backgroundColor: `${colors.action.hover} !important`,
                                  },
                                  "&[aria-selected='true']": {
                                    backgroundColor: `${colors.action.hover} !important`,
                                  },
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select an email template"
                              inputProps={{
                                ...params.inputProps,
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingTemplates ? (
                                      <CircularProgress color="inherit" size={18} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {option.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.subject}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          noOptionsText={
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                No templates available
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                      {emailTemplatePermission && (
                        <Grid item xs={12}>
                          <Box
                            component="a"
                            onClick={handleCreateEmail}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: colors.primary.main,
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              cursor: 'pointer',

                              '&:hover': {
                                textDecoration: 'underline',
                                color: colors.primary.dark,
                              },
                            }}
                          >
                            <Add sx={{ fontSize: 18 }} />
                            Create New Email Template
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* WhatsApp Template Section - Show only when whatsapp channel is selected */}
                {selectedChannel === COMMUNICATION_CHANNEL.WHATSAPP && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: 500,
                        color: formErrors.whatsapp_template
                          ? colors.error.main
                          : colors.text.primary,
                      }}
                    >
                      WhatsApp Template <span style={{ color: colors.error.main }}>*</span>
                    </Typography>
                    {formErrors.whatsapp_template && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.error.main,
                          display: 'block',
                          mb: 1,
                          fontSize: '0.75rem',
                        }}
                      >
                        {formErrors.whatsapp_template}
                      </Typography>
                    )}
                    <Autocomplete
                      size="small"
                      options={whatsappTemplates}
                      getOptionLabel={(option) => option.name}
                      value={selectedWhatsappTemplate}
                      onChange={(event, newValue) => setSelectedWhatsappTemplate(newValue)}
                      loading={loadingWhatsappTemplates}
                      openOnFocus
                      ListboxProps={{
                        style: {
                          maxHeight: '250px',
                        },
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            borderRadius: '4px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            '& .MuiAutocomplete-option': {
                              py: 1.5,
                              backgroundColor: 'transparent !important',
                              '&:hover': {
                                backgroundColor: `${colors.action.hover} !important`,
                              },
                              "&[aria-selected='true']": {
                                backgroundColor: `${colors.action.hover} !important`,
                              },
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select a WhatsApp template"
                          inputProps={{
                            ...params.inputProps,
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingWhatsappTemplates ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              width: '100%',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mr: 1,
                                mt: 0.5,
                              }}
                            >
                              {getWhatsAppTemplateTypeIcon(option.type)}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.text.primary,
                                  fontWeight: 500,
                                }}
                              >
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  color: colors.text.secondary,
                                  fontSize: '0.875rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {truncateText(option.body, 80) || 'No description'}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      noOptionsText={
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No WhatsApp templates available
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Tags removed from create form - will be added via Enroll Contacts */}
        </Grid>
      </Box>
    );
  }

  const filteredContacts = getSortedAndFilteredContacts();
  const paginatedContacts = getPaginatedContacts();
  const totalPages = getTotalPages();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        p: { xs: 1, sm: 2, md: 2 },
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate('/campaigns')}
            size="small"
            sx={{
              '&:hover': { color: 'rgba(0, 0, 0, 1)' },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#000000',
                  fontSize: '1.25rem',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  textTransform: 'capitalize',
                }}
              >
                {campaign?.name || 'Campaign Details'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1.5}>
          {campaign?.status === 'DRAFT' && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (enrollmentType === 'ALL' || enrollmentType === 'MANUAL') {
                    loadAllContacts();
                  }
                  setEnrollModalOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  fontWeight: 500,
                }}
              >
                Enroll Contacts
              </Button>
              <Box
                component="a"
                href={`/campaigns/${id}?edit=true`}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditMode();
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  py: 0.75,
                  backgroundColor: colors.primary.main,
                  color: colors.primary.contrastText,
                  borderRadius: '4px',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: colors.primary.dark,
                  },
                }}
              >
                Edit
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleActivateCampaign}
                disabled={loading || (campaign?.total_contacts || 0) === 0}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  fontWeight: 500,
                  backgroundColor: colors.success.main,
                  '&:hover': {
                    backgroundColor: colors.success.dark,
                  },
                }}
              >
                Activate Campaign
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Campaign Statistics */}
      {campaign && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: `repeat(${
                  campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL
                    ? '6'
                    : campaign?.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP
                      ? '5'
                      : '1'
                }, 1fr)`,
                xl: `repeat(${
                  campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL
                    ? '6'
                    : campaign?.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP
                      ? '5'
                      : '1'
                }, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {/* Total Contacts - Always show */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: '4px',
                boxShadow: 'none',
                backgroundColor: '#F7FAFC',
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  display: 'block',
                }}
              >
                Total Contacts
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#2D3748', fontSize: '1.2rem' }}
              >
                {campaign.total_contacts || 0}
              </Typography>
            </Card>

            {/* Email Stats - Show only if campaign is email campaign */}
            {campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL && (
              <>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    backgroundColor: '#F7FAFC',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: 'block',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    Emails Sent
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.2rem',
                    }}
                  >
                    {campaign.email_statistics?.sent || 0}
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    backgroundColor: '#F7FAFC',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: 'block',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    Email Opened
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.2rem',
                    }}
                  >
                    {campaign.email_statistics?.opened || 0}
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    backgroundColor: '#F7FAFC',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: 'block',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    Emails Bounced
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.2rem',
                    }}
                  >
                    {campaign.email_statistics?.bounced || 0}
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    backgroundColor: '#F7FAFC',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: 'block',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    Emails Failed
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.2rem',
                    }}
                  >
                    {campaign.email_statistics?.failed || 0}
                  </Typography>
                </Card>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    backgroundColor: '#F7FAFC',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: 'block',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    Emails Unsubscribed
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.2rem',
                    }}
                  >
                    {campaign.email_statistics?.unsubscribe || 0}
                  </Typography>
                </Card>
              </>
            )}

            {/* WhatsApp Stats - Show only if campaign is WhatsApp campaign */}
            {campaign?.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP &&
              campaign?.whatsapp_statistics && (
                <>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: '4px',
                      boxShadow: 'none',
                      backgroundColor: '#F7FAFC',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      Messages Sent
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2D3748',
                        fontSize: '1.2rem',
                      }}
                    >
                      {campaign.whatsapp_statistics.sent || 0}
                    </Typography>
                  </Card>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: '4px',
                      boxShadow: 'none',
                      backgroundColor: '#F7FAFC',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      Messages Delivered
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2D3748',
                        fontSize: '1.2rem',
                      }}
                    >
                      {campaign.whatsapp_statistics.delivered || 0}
                    </Typography>
                  </Card>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: '4px',
                      boxShadow: 'none',
                      backgroundColor: '#F7FAFC',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      Messages Read
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2D3748',
                        fontSize: '1.2rem',
                      }}
                    >
                      {campaign.whatsapp_statistics.read || 0}
                    </Typography>
                  </Card>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: '4px',
                      boxShadow: 'none',
                      backgroundColor: '#F7FAFC',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                        }}
                      >
                        Messages Failed
                      </Typography>
                      {campaign.status === 'COMPLETED' &&
                        campaign.whatsapp_statistics.failed > 0 && (
                          <Tooltip title="Retry failed messages">
                            <IconButton
                              size="small"
                              onClick={handleOpenRetryModal}
                              sx={{
                                ml: 1,
                                p: 0.5,
                                color: colors.primary,
                                '&:hover': {
                                  backgroundColor: 'rgba(54, 162, 235, 0.1)',
                                },
                              }}
                            >
                              <FiAlertTriangle size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2D3748',
                        fontSize: '1.2rem',
                      }}
                    >
                      {campaign.whatsapp_statistics.failed || 0}
                    </Typography>
                  </Card>
                </>
              )}
          </Box>
        </Box>
      )}

      {/* Tabs */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Campaign Details" />
          <Tab label="Campaign Contacts" />
        </Tabs>
      </Box>

      {/* Draft Campaign Info Pill */}
      {campaign?.statusInfo?.showDraftWarning && (
        <Alert
          severity="info"
          icon={<InfoOutlined />}
          sx={{
            mt: 2,
            borderRadius: '4px',
            backgroundColor: `${colors.primary.main}08`,
            border: `1px solid ${colors.primary.main}30`,
            '& .MuiAlert-icon': {
              color: colors.primary.main,
            },
            '& .MuiAlert-message': {
              color: colors.text.primary,
              fontWeight: 500,
            },
          }}
        >
          {campaign.statusInfo.draftWarningMessage}
        </Alert>
      )}

      {/* Tab 1: Campaign Details */}
      <Card
        sx={{
          mt: 2,
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 3 }}>
            {/* Campaign Metadata Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 3,
                mb: 3,
              }}
            >
              {/* Campaign Name */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    fontSize: '14px !important',
                  }}
                >
                  Campaign Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: '1rem',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {campaign?.name}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    fontSize: '14px !important',
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={campaign?.status}
                  size="small"
                  sx={{
                    fontSize: '0.85rem',
                    backgroundColor:
                      campaign?.status === 'COMPLETED'
                        ? `${colors.success.main}15`
                        : campaign?.status === 'ACTIVE'
                          ? `${colors.error.main}15`
                          : colors.action.hover,
                    color:
                      campaign?.status === 'COMPLETED'
                        ? colors.success.main
                        : campaign?.status === 'ACTIVE'
                          ? colors.error.main
                          : colors.text.secondary,
                    fontWeight: 600,
                    borderRadius: '4px',
                  }}
                />
              </Box>

              {/* Communication Channels */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                    fontSize: '14px !important',
                  }}
                >
                  Communication Channels
                </Typography>
                <Box display="flex" gap={1.5} alignItems="center">
                  {campaign?.email_template_id && (
                    <Tooltip title="Email" arrow>
                      <Box
                        sx={{
                          color: colors.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <FiMail size={20} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            colors: colors.text.primary,
                          }}
                        >
                          Email
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {campaign?.whatsapp_template_id && (
                    <Tooltip title="WhatsApp" arrow>
                      <Box
                        sx={{
                          color: '#25D366',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <FaWhatsapp size={20} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          WhatsApp
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {!campaign?.email_template_id && !campaign?.whatsapp_template_id && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      --
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Description - Full Width */}
            <Box mb={3}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 500, fontSize: '14px !important' }}
              >
                Description
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: colors.text.primary,
                  fontSize: '1rem',
                }}
              >
                {campaign?.description || 'No description'}
              </Typography>
            </Box>

            {/* Tags - Show only if campaign has tags */}
            {campaign?.tags && campaign.tags.length > 0 && (
              <Box mb={3}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontWeight: 600,
                    fontSize: '14px !important',
                  }}
                >
                  Tags
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {campaign.tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        backgroundColor: tag.color || 'rgba(139, 95, 191, 0.1)',
                        color: tag.color ? '#fff' : '#8B5FBF',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Collapsible Email Template Section - Only show if campaign has email template */}
            {campaign?.email_template_id && (
              <Box mb={3}>
                <Box
                  onClick={() => setEmailSectionOpen(!emailSectionOpen)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    borderRadius: '4px',
                    backgroundColor: emailSectionOpen ? '#FFFFFF' : '#FAFAFA',
                    border: emailSectionOpen ? `1px solid ${colors.primary.main}` : 'none',
                    '&:hover': {
                      color: colors.primary.main,
                      cursor: 'pointer',
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px !important',
                      color: colors.text.primary,
                    }}
                  >
                    Campaign Email Template
                  </Typography>
                  <IconButton size="medium" sx={{ backgroundColor: 'transparent !important' }}>
                    {emailSectionOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={emailSectionOpen}>
                  <Box
                    sx={{
                      mt: '1px',
                      p: 2,
                      backgroundColor: '#FAFAFA',
                      borderRadius: '0 0 4px 4px',
                    }}
                  >
                    {emailLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : !emailTemplate ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No email template associated with this campaign
                      </Typography>
                    ) : (
                      <Box>
                        <Box mb={2}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontWeight: 500,
                              fontSize: '14px !important',
                            }}
                          >
                            Template Name
                          </Typography>
                          <Typography
                            variant="body2"
                            color={colors.text.primary}
                            sx={{ fontWeight: 500, fontSize: '0.95rem' }}
                          >
                            {emailTemplate.name}
                          </Typography>
                        </Box>

                        <Box mb={2}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontWeight: 500,
                              fontSize: '14px !important',
                            }}
                          >
                            Subject
                          </Typography>
                          <Typography
                            variant="body2"
                            color={colors.text.primary}
                            sx={{ fontWeight: 500, fontSize: '0.95rem' }}
                          >
                            {emailTemplate.subject}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 1,
                              fontWeight: 500,
                              fontSize: '14px !important',
                            }}
                          >
                            Email Preview
                          </Typography>
                          <Card
                            sx={{
                              border: '1px solid #E2E8F0',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              overflow: 'hidden',
                            }}
                          >
                            <iframe
                              title="Email Preview"
                              srcDoc={emailTemplate.html_body}
                              style={{
                                width: '100%',
                                minHeight: '400px',
                                maxHeight: '600px',
                                border: 'none',
                                display: 'block',
                              }}
                              onLoad={(e) => {
                                const iframe = e.target;
                                try {
                                  const height = iframe.contentWindow.document.body.scrollHeight;
                                  iframe.style.height = Math.min(Math.max(height, 400), 600) + 'px';

                                  // Disable clicks on links and buttons only (preserve scrolling)
                                  const iframeDoc = iframe.contentWindow.document;
                                  const style = iframeDoc.createElement('style');
                                  style.textContent =
                                    'a, button, input, select, textarea { pointer-events: none !important; cursor: default !important; }';
                                  iframeDoc.head.appendChild(style);

                                  // Prevent all link clicks
                                  iframeDoc.addEventListener(
                                    'click',
                                    (event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      return false;
                                    },
                                    true,
                                  );
                                } catch (err) {
                                  iframe.style.height = '500px';
                                }
                              }}
                            />
                          </Card>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Collapsible WhatsApp Template Section - Only show if campaign has whatsapp template */}
            {campaign?.whatsapp_template_id && (
              <Box mb={3}>
                <Box
                  onClick={() => setWhatsappSectionOpen(!whatsappSectionOpen)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    borderRadius: '4px',
                    backgroundColor: whatsappSectionOpen ? '#FFFFFF' : '#FAFAFA',
                    border: whatsappSectionOpen ? `1px solid ${colors.primary.main}` : 'none',
                    '&:hover': {
                      color: colors.primary.main,
                      cursor: 'pointer',
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px !important',
                      color: colors.text.primary,
                    }}
                  >
                    Campaign WhatsApp Template
                  </Typography>
                  <IconButton size="medium" sx={{ backgroundColor: 'transparent !important' }}>
                    {whatsappSectionOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={whatsappSectionOpen}>
                  <Box
                    sx={{
                      mt: '1px',
                      p: 2,
                      backgroundColor: '#FAFAFA',
                      borderRadius: '0 0 4px 4px',
                    }}
                  >
                    {whatsappLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : !whatsappTemplate ? (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No WhatsApp template associated with this campaign
                      </Typography>
                    ) : (
                      <WhatsAppTemplatePreview template={whatsappTemplate} />
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Campaign Contacts */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2 }}>
            {/* Search Bar and Event Filter */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: '4px',
                boxShadow: 'none',
                border: `1px solid ${colors.border.main}`,
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyFilters();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchTerm ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchTerm('');
                                setAppliedSearchTerm('');
                                setContactsPage(1);
                              }}
                              sx={{
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: colors.action.hover,
                                },
                              }}
                            >
                              <FiX size={16} />
                            </IconButton>
                          ) : (
                            <Search fontSize="small" sx={{ color: colors.text.secondary }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Hide event filter for DRAFT campaigns */}
                {campaign?.status !== 'DRAFT' && (
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Filter by Event"
                      value={eventFilter}
                      onChange={(e) => {
                        setEventFilter(e.target.value);
                      }}
                    >
                      <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                        All Events
                      </MenuItem>
                      {campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL
                        ? [
                            <MenuItem
                              key="send"
                              value="send"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Email Sent
                            </MenuItem>,
                            <MenuItem
                              key="open"
                              value="open"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Email Opened
                            </MenuItem>,
                            <MenuItem
                              key="bounce"
                              value="bounce"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Email Bounced
                            </MenuItem>,
                            <MenuItem
                              key="unsubscribe"
                              value="unsubscribe"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Unsubscribe
                            </MenuItem>,
                            <MenuItem
                              key="failed"
                              value="failed"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Email Failed
                            </MenuItem>,
                          ]
                        : [
                            <MenuItem
                              key="send"
                              value="send"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Message Sent
                            </MenuItem>,
                            <MenuItem
                              key="delivered"
                              value="delivered"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Message Delivered
                            </MenuItem>,
                            <MenuItem
                              key="read"
                              value="read"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Message Read
                            </MenuItem>,
                            <MenuItem
                              key="failed"
                              value="failed"
                              sx={{ p: '2px 8px', minHeight: '32px' }}
                            >
                              Message Failed
                            </MenuItem>,
                          ]}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12} md={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={handleApplyFilters}
                      disabled={!hasFilterChanges}
                      sx={{
                        backgroundColor: 'transparent',
                        color: colors.primary.main,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 510,
                        p: '8px',
                        '&:disabled': {
                          color: colors.text.disabled,
                        },
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      Apply
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleClearFilters}
                      disabled={!appliedSearchTerm && !appliedEventFilter}
                      sx={{
                        backgroundColor: 'transparent',
                        color: colors.primary.main,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 510,
                        p: '8px',
                        '&:disabled': {
                          color: colors.text.disabled,
                        },
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      Clear
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={12} md>
                  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => setContactsPage(Math.max(1, contactsPage - 1))}
                      disabled={contactsPage === 1}
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                        },
                        '&:disabled': {
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        },
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.text.secondary,
                        minWidth: '80px',
                        textAlign: 'center',
                        fontSize: '0.85rem',
                      }}
                    >
                      Page {contactsPage} of {totalPages || 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setContactsPage(Math.min(totalPages, contactsPage + 1))}
                      disabled={contactsPage >= totalPages}
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                        },
                        '&:disabled': {
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        },
                      }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Contacts Table with Loading Overlay */}
            <Box sx={{ position: 'relative' }}>
              {contactsLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 10,
                    borderRadius: '8px',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {filteredContacts.length === 0 && !contactsLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {appliedSearchTerm
                      ? 'No contacts match your search'
                      : 'No contacts associated with this campaign yet'}
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 'none',
                    border: `1px solid ${colors.border.main}`,
                    borderRadius: '8px',
                    maxHeight: 'calc(100vh - 400px)',
                    overflow: 'auto',
                  }}
                >
                  <Table sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: colors.background.paper,
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: colors.text.primary,
                            cursor: 'pointer',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                          onClick={() => handleSort('contact_name')}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            Contact Name
                            {getSortIcon(sortConfig.key, sortConfig.direction, 'contact_name')}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: colors.text.primary,
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {campaign?.whatsapp_template_id && !campaign?.email_template_id
                            ? 'Phone'
                            : 'Email'}
                        </TableCell>
                        {campaign?.status !== 'DRAFT' && (
                          <>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: colors.text.primary,
                                whiteSpace: 'nowrap',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                width: '100px',
                              }}
                            >
                              {campaign?.whatsapp_template_id && !campaign?.email_template_id
                                ? 'Sent'
                                : 'Email Sent'}
                            </TableCell>
                            {/* Delivered column - only for WhatsApp campaigns */}
                            {campaign?.whatsapp_template_id && !campaign?.email_template_id && (
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  width: '100px',
                                }}
                              >
                                Delivered
                              </TableCell>
                            )}
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: colors.text.primary,
                                whiteSpace: 'nowrap',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                width: '100px',
                              }}
                            >
                              {campaign?.whatsapp_template_id && !campaign?.email_template_id
                                ? 'Read'
                                : 'Email Opened'}
                            </TableCell>
                            {/* Only show Bounced for Email campaigns (or mixed) */}
                            {(!campaign?.whatsapp_template_id || campaign?.email_template_id) && (
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  width: '100px',
                                }}
                              >
                                Email Bounced
                              </TableCell>
                            )}
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color: colors.text.primary,
                                whiteSpace: 'nowrap',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                width: '100px',
                              }}
                            >
                              {campaign?.whatsapp_template_id && !campaign?.email_template_id
                                ? 'Failed'
                                : 'Email Failed'}
                            </TableCell>
                            {/* Only show Unsubscribe for Email campaigns (or mixed) */}
                            {campaign?.email_template_id && (
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  width: '120px',
                                }}
                              >
                                Email Unsubscribe
                              </TableCell>
                            )}
                            {/* Failure Reason column only for whatsapp campaigns */}
                            {campaign?.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP && (
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  minWidth: '150px',
                                }}
                              >
                                Failure Reason
                              </TableCell>
                            )}
                            {/* Retry Attempts column - only for WhatsApp campaigns */}
                            {campaign.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP && (
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  color: colors.text.primary,
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  width: '120px',
                                }}
                              >
                                Retry Attempts
                              </TableCell>
                            )}
                          </>
                        )}
                        {campaign?.status === 'DRAFT' && (
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 600,
                              color: colors.text.primary,
                              whiteSpace: 'nowrap',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              width: '100px',
                            }}
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedContacts.map((contact, index) => (
                        <TableRow
                          key={contact.id}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: colors.action.row },
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:not(:last-of-type)': {
                              borderBottom: `1px solid ${colors.border.main}`,
                            },
                          }}
                          onClick={() => navigateWithPermission(`/contacts/${contact.id}`)}
                        >
                          <TableCell
                            sx={{
                              color: colors.text.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {contact.contact_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: colors.text.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {campaign?.whatsapp_template_id && !campaign?.email_template_id
                              ? `+${contact.contact_phone}` || '-'
                              : contact.primary_email || '-'}
                          </TableCell>
                          {campaign?.status !== 'DRAFT' && (
                            <>
                              <TableCell align="center">
                                {((campaign?.whatsapp_template_id && !campaign?.email_template_id
                                  ? contact.messages_sent
                                  : contact.emails_sent) || 0) > 0 ? (
                                  <CheckCircle
                                    sx={{
                                      color: colors.success.main,
                                      fontSize: '1.1rem !important',
                                    }}
                                  />
                                ) : (
                                  <CancelIcon
                                    sx={{
                                      color: colors.text.disabled,
                                      fontSize: '1.1rem !important',
                                    }}
                                  />
                                )}
                              </TableCell>
                              {/* Delivered cell - only for WhatsApp campaigns */}
                              {campaign?.whatsapp_template_id && !campaign?.email_template_id && (
                                <TableCell align="center">
                                  {(contact.messages_delivered || 0) > 0 ? (
                                    <CheckCircle
                                      sx={{
                                        color: colors.success.main,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  ) : (
                                    <CancelIcon
                                      sx={{
                                        color: colors.text.disabled,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                {((campaign?.whatsapp_template_id && !campaign?.email_template_id
                                  ? contact.messages_read
                                  : contact.emails_opened) || 0) > 0 ? (
                                  <CheckCircle
                                    sx={{
                                      color: colors.primary.main,
                                      fontSize: '1.1rem !important',
                                    }}
                                  />
                                ) : (
                                  <CancelIcon
                                    sx={{
                                      color: colors.text.disabled,
                                      fontSize: '1.1rem !important',
                                    }}
                                  />
                                )}
                              </TableCell>
                              {/* Only show Bounced for Email campaigns (or mixed) */}
                              {(!campaign?.whatsapp_template_id || campaign?.email_template_id) && (
                                <TableCell align="center">
                                  {contact.emails_bounced > 0 ? (
                                    <CheckCircle
                                      sx={{
                                        color: colors.error.main,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  ) : (
                                    <CancelIcon
                                      sx={{
                                        color: colors.text.disabled,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                {((campaign?.whatsapp_template_id && !campaign?.email_template_id
                                  ? contact.messages_failed
                                  : contact.emails_failed) || 0) > 0 ? (
                                  <Tooltip
                                    title={contact.failure_reason || 'Message delivery failed'}
                                    placement="top"
                                    arrow
                                  >
                                    <CheckCircle
                                      sx={{
                                        color: '#DC2626',
                                        fontSize: '1.1rem !important',
                                        cursor: 'help',
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <CancelIcon
                                    sx={{
                                      color: colors.text.disabled,
                                      fontSize: '1.1rem !important',
                                    }}
                                  />
                                )}
                              </TableCell>
                              {/* Only show Unsubscribe for Email campaigns (or mixed) */}
                              {campaign?.email_template_id && (
                                <TableCell align="center">
                                  {(contact.emails_unsubscribed || 0) > 0 ? (
                                    <CheckCircle
                                      sx={{
                                        color: colors.warning.main,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  ) : (
                                    <CancelIcon
                                      sx={{
                                        color: colors.text.disabled,
                                        fontSize: '1.1rem !important',
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              {/* Failure Reason cell - only for WhatsApp campaigns */}
                              {campaign?.communication_channel ===
                                COMMUNICATION_CHANNEL.WHATSAPP && (
                                <TableCell
                                  sx={{
                                    color: colors.text.secondary,
                                    fontSize: '0.875rem',
                                    maxWidth: '250px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <Tooltip
                                    title={contact.failure_reason || '-'}
                                    placement="top"
                                    arrow
                                  >
                                    <span>{contact.failure_reason || '-'}</span>
                                  </Tooltip>
                                </TableCell>
                              )}
                              {/* Retry Attempts cell - only for WhatsApp campaigns */}
                              {campaign?.communication_channel ===
                                COMMUNICATION_CHANNEL.WHATSAPP && (
                                <TableCell align="center">
                                  {contact.pick_attempt > 1 ? (
                                    <Tooltip
                                      title={`Original send + ${
                                        contact.pick_attempt - 1
                                      } retry attempt${contact.pick_attempt - 1 > 1 ? 's' : ''}`}
                                      placement="top"
                                      arrow
                                    >
                                      <Chip
                                        label={contact.pick_attempt - 1}
                                        size="small"
                                        sx={{
                                          backgroundColor: colors.warning.light,
                                          color: colors.warning.dark,
                                          fontWeight: 600,
                                          fontSize: '0.75rem',
                                          height: '24px',
                                          minWidth: '32px',
                                        }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {contact.pick_attempt === 1 ? '0' : '-'}
                                    </Typography>
                                  )}
                                </TableCell>
                              )}
                            </>
                          )}
                          {campaign?.status === 'DRAFT' && (
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContactToUnenroll(contact);
                                  setDeleteConfirmOpen(true);
                                }}
                                sx={{
                                  color: colors.error.main,
                                  '&:hover': {
                                    color: colors.error.dark,
                                  },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        </TabPanel>
      </Card>

      {/* Activation Confirmation Modal */}
      <Dialog
        open={confirmActivateOpen}
        onClose={() => !loading && setConfirmActivateOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '4px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '100%',
            maxWidth: '430px',
            m: 2,
          },
        }}
      >
        <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
          {!loading && (
            <IconButton
              onClick={() => setConfirmActivateOpen(false)}
              size="small"
              sx={{
                color: colors.text.secondary,
                '&:hover': {
                  color: colors.text.primary,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <FiX size={18} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ p: 4, textAlign: 'center', borderRadius: '4px !important' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '4px !important',
              backgroundColor: colors.success.main + '15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <FiAlertTriangle size={24} color={colors.success.main} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              fontSize: '1.25rem',
              mb: 1.5,
              lineHeight: 1.3,
            }}
          >
            Activate Campaign?
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              px: 1,
              mb: 4,
              fontSize: '0.95rem',
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to activate <strong>"{campaign?.name}"</strong>? This will
            immediately start sending{' '}
            {campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL
              ? 'emails'
              : 'WhatsApp messages'}{' '}
            to <strong>{campaign?.total_contacts || 0} contact(s)</strong>. This action cannot be
            undone.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setConfirmActivateOpen(false)}
              variant="outlined"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.2,
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                borderColor: colors.border.main,
                color: colors.text.secondary,
                '&:hover': {
                  borderColor: colors.text.primary,
                  backgroundColor: colors.background.light,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmActivate}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.2,
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                backgroundColor: colors.success.main,
                '&:hover': {
                  backgroundColor: colors.success.dark,
                },
              }}
            >
              {loading ? 'Activating...' : 'Yes, Activate'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Contact Unenroll Confirmation */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => !unenrolling && setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmUnenroll}
        title="Remove Contact?"
        message={`Are you sure you want to remove ${
          contactToUnenroll?.contact_name || 'this contact'
        } from the campaign?`}
        itemName="contact"
        deleting={unenrolling}
      />

      {/* Enroll Contacts Modal */}
      <Dialog
        open={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          Enroll Contacts
          <IconButton
            onClick={() => setEnrollModalOpen(false)}
            size="small"
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                color: colors.text.primary,
                backgroundColor: colors.action.hover,
              },
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            {(() => {
              const isEmailCampaign =
                campaign?.email_template_id !== null && campaign?.email_template_id !== undefined;
              const isWhatsAppCampaign =
                campaign?.whatsapp_template_id !== null &&
                campaign?.whatsapp_template_id !== undefined;

              if (isEmailCampaign && !isWhatsAppCampaign) {
                return 'Select how you want to enroll contacts. Contacts without valid email addresses or with "Do Not Email" enabled will be skipped.';
              } else if (isWhatsAppCampaign && !isEmailCampaign) {
                return 'Select how you want to enroll contacts. Only contacts with valid phone numbers will be enrolled.';
              } else if (isEmailCampaign && isWhatsAppCampaign) {
                return 'Select how you want to enroll contacts. Contacts must have either a valid email address or phone number.';
              }
              return 'Select how you want to enroll contacts.';
            })()}
          </Alert>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ fontWeight: 500, mb: 1 }}>
              Enrollment Method
            </FormLabel>
            <RadioGroup
              row
              value={enrollmentType}
              onChange={(e) => {
                setEnrollmentType(e.target.value);
                if (
                  (e.target.value === 'ALL' || e.target.value === 'MANUAL') &&
                  allContacts.length === 0
                ) {
                  loadAllContacts();
                }
              }}
            >
              <FormControlLabel value="ALL" control={<Radio />} label="All Contacts" />
              <FormControlLabel value="TAGS" control={<Radio />} label="By Tags" />
              <FormControlLabel value="MANUAL" control={<Radio />} label="Manual Selection" />
            </RadioGroup>
          </FormControl>

          {/* All Contacts Selection */}
          {enrollmentType === 'ALL' && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: colors.background.default,
                borderRadius: '4px',
                borderLeft: `4px solid ${colors.primary.main}`,
              }}
            >
              {loadingAllContacts ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Calculating total eligible contacts...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: colors.text.primary, fontWeight: 500 }}>
                  This will enroll all <strong>{allContacts.length}</strong> eligible contacts
                  currently not in this campaign
                  {(() => {
                    const isEmailCampaign =
                      campaign?.email_template_id !== null &&
                      campaign?.email_template_id !== undefined;
                    const isWhatsAppCampaign =
                      campaign?.whatsapp_template_id !== null &&
                      campaign?.whatsapp_template_id !== undefined;

                    if (isEmailCampaign && !isWhatsAppCampaign) {
                      return ' (with valid email addresses)';
                    } else if (isWhatsAppCampaign && !isEmailCampaign) {
                      return ' (with valid phone numbers)';
                    } else if (isEmailCampaign && isWhatsAppCampaign) {
                      return ' (with either email or phone number)';
                    }
                    return '';
                  })()}
                  .
                </Typography>
              )}
            </Box>
          )}

          {/* Tags Selection */}
          {enrollmentType === 'TAGS' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                Select Tags
              </Typography>
              <TagAutocomplete
                options={availableTags.filter(
                  (tag) => !selectedTags.some((selected) => selected.id === tag.id),
                )}
                value={selectedTags}
                onChange={(event, newValue) => setSelectedTags(newValue)}
                loading={loadingTags}
                placeholder="Select tags to enroll contacts"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Contacts with at least one of these tags will be enrolled
                {(() => {
                  const isEmailCampaign =
                    campaign?.email_template_id !== null &&
                    campaign?.email_template_id !== undefined;
                  const isWhatsAppCampaign =
                    campaign?.whatsapp_template_id !== null &&
                    campaign?.whatsapp_template_id !== undefined;

                  if (isEmailCampaign && !isWhatsAppCampaign) {
                    return ' (only those with valid email addresses)';
                  } else if (isWhatsAppCampaign && !isEmailCampaign) {
                    return ' (only those with valid phone numbers)';
                  } else if (isEmailCampaign && isWhatsAppCampaign) {
                    return ' (with either email or phone number)';
                  }
                  return '';
                })()}
                .
              </Typography>
            </Box>
          )}

          {/* Manual Contact Selection */}
          {enrollmentType === 'MANUAL' && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Select Contacts Manually
                  </Typography>
                  <Chip
                    label={`${selectedContactIds.length} selected`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600, borderRadius: '4px' }}
                  />
                </Box>

                <TextField
                  fullWidth
                  placeholder={(() => {
                    const isEmailCampaign =
                      campaign?.email_template_id !== null &&
                      campaign?.email_template_id !== undefined;
                    const isWhatsAppCampaign =
                      campaign?.whatsapp_template_id !== null &&
                      campaign?.whatsapp_template_id !== undefined;

                    if (isEmailCampaign && !isWhatsAppCampaign) {
                      return 'Search by name or email...';
                    } else if (isWhatsAppCampaign && !isEmailCampaign) {
                      return 'Search by name or phone...';
                    } else {
                      return 'Search by name, email or phone...';
                    }
                  })()}
                  size="small"
                  value={enrollmentSearch}
                  onChange={(e) => {
                    setEnrollmentSearch(e.target.value);
                    setEnrollmentPage(1); // Reset to first page on search
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.background.default,
                    },
                  }}
                />
              </Box>

              {loadingAllContacts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {(() => {
                    const filteredContacts = allContacts.filter((contact) => {
                      const searchStr = enrollmentSearch.toLowerCase();
                      const name =
                        contact.type === 'INDIVIDUAL'
                          ? `${contact.first_name || ''} ${contact.last_name || ''}`
                              .trim()
                              .toLowerCase()
                          : (contact.organization_name || '').toLowerCase();
                      const email = (contact.primary_email || '').toLowerCase();
                      const phone = (contact.phone_1 || '').toLowerCase();
                      return (
                        name.includes(searchStr) ||
                        email.includes(searchStr) ||
                        phone.includes(searchStr)
                      );
                    });

                    return (
                      <>
                        <TableContainer
                          component={Paper}
                          sx={{
                            mt: 2,
                            border: `1px solid ${colors.border.main}`,
                            height: '400px',
                            overflowY: 'auto',
                          }}
                        >
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  padding="checkbox"
                                  sx={{
                                    paddingLeft: 0,
                                    width: '2%',
                                  }}
                                >
                                  <Checkbox
                                    indeterminate={
                                      selectedContactIds.length > 0 &&
                                      selectedContactIds.length < filteredContacts.length
                                    }
                                    checked={
                                      filteredContacts.length > 0 &&
                                      selectedContactIds.length >= filteredContacts.length &&
                                      filteredContacts.every((c) =>
                                        selectedContactIds.includes(c.id),
                                      )
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        // Add currently filtered IDs to selection
                                        const newIds = [
                                          ...new Set([
                                            ...selectedContactIds,
                                            ...filteredContacts.map((c) => c.id),
                                          ]),
                                        ];
                                        setSelectedContactIds(newIds);
                                      } else {
                                        // Remove currently filtered IDs from selection
                                        const filteredIds = new Set(
                                          filteredContacts.map((c) => c.id),
                                        );
                                        setSelectedContactIds(
                                          selectedContactIds.filter((id) => !filteredIds.has(id)),
                                        );
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>Name</TableCell>
                                {(() => {
                                  const isEmailCampaign =
                                    campaign?.email_template_id !== null &&
                                    campaign?.email_template_id !== undefined;
                                  const isWhatsAppCampaign =
                                    campaign?.whatsapp_template_id !== null &&
                                    campaign?.whatsapp_template_id !== undefined;

                                  if (isEmailCampaign) {
                                    return <TableCell>Email</TableCell>;
                                  }
                                  if (isWhatsAppCampaign && !isEmailCampaign) {
                                    return <TableCell>Phone</TableCell>;
                                  }
                                  return <TableCell>Contact Info</TableCell>;
                                })()}
                                <TableCell>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredContacts.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      No contacts found matching "{enrollmentSearch}"
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredContacts
                                  .slice(
                                    (enrollmentPage - 1) * enrollmentItemsPerPage,
                                    enrollmentPage * enrollmentItemsPerPage,
                                  )
                                  .map((contact) => (
                                    <TableRow key={contact.id} hover>
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          checked={selectedContactIds.includes(contact.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedContactIds([
                                                ...selectedContactIds,
                                                contact.id,
                                              ]);
                                            } else {
                                              setSelectedContactIds(
                                                selectedContactIds.filter(
                                                  (cid) => cid !== contact.id,
                                                ),
                                              );
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {contact.type === 'INDIVIDUAL'
                                          ? `${contact.first_name || ''} ${
                                              contact.last_name || ''
                                            }`.trim()
                                          : contact.organization_name}
                                      </TableCell>
                                      <TableCell>
                                        {(() => {
                                          const isEmailCampaign =
                                            campaign?.email_template_id !== null &&
                                            campaign?.email_template_id !== undefined;
                                          const isWhatsAppCampaign =
                                            campaign?.whatsapp_template_id !== null &&
                                            campaign?.whatsapp_template_id !== undefined;

                                          if (isEmailCampaign && !isWhatsAppCampaign) {
                                            return contact.primary_email || '-';
                                          } else if (isWhatsAppCampaign && !isEmailCampaign) {
                                            return contact.phone_1 || '-';
                                          } else {
                                            return contact.primary_email || contact.phone_1 || '-';
                                          }
                                        })()}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={contact.type}
                                          size="small"
                                          color={
                                            contact.type === 'INDIVIDUAL' ? 'primary' : 'secondary'
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Pagination Controls */}
                        {filteredContacts.length > enrollmentItemsPerPage && (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap={2}
                            mt={2}
                          >
                            <IconButton
                              size="small"
                              onClick={() => setEnrollmentPage(Math.max(1, enrollmentPage - 1))}
                              disabled={enrollmentPage === 1}
                              sx={{
                                '&:disabled': {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <ChevronLeft />
                            </IconButton>
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.text.secondary,
                                minWidth: '100px',
                                textAlign: 'center',
                              }}
                            >
                              Page {enrollmentPage} of{' '}
                              {Math.ceil(filteredContacts.length / enrollmentItemsPerPage)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEnrollmentPage(
                                  Math.min(
                                    Math.ceil(filteredContacts.length / enrollmentItemsPerPage),
                                    enrollmentPage + 1,
                                  ),
                                )
                              }
                              disabled={
                                enrollmentPage >=
                                Math.ceil(filteredContacts.length / enrollmentItemsPerPage)
                              }
                              sx={{
                                '&:disabled': {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <ChevronRight />
                            </IconButton>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              borderRadius: '4px',
            }}
            onClick={() => {
              setEnrollModalOpen(false);
              setSelectedTags([]);
              setSelectedContactIds([]);
              setEnrollmentPage(1);
              setEnrollmentSearch('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEnrollContacts}
            disabled={
              loading ||
              (enrollmentType === 'TAGS' && selectedTags.length === 0) ||
              (enrollmentType === 'MANUAL' && selectedContactIds.length === 0)
            }
            sx={{
              borderRadius: '4px',
              backgroundColor: colors.primary.main,
              '&:hover': {
                backgroundColor: colors.primary.dark,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : enrollmentType === 'ALL' ? (
              `Enroll All ${allContacts.length} Contacts`
            ) : (
              'Enroll Contacts'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Failed WhatsApp Messages Modal */}
      <Dialog
        open={retryModalOpen}
        onClose={() => !retrying && setRetryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          Retry Failed WhatsApp Messages
          <IconButton
            onClick={() => setRetryModalOpen(false)}
            size="small"
            disabled={retrying}
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                color: colors.text.primary,
                backgroundColor: colors.action.hover,
              },
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Select error groups below to retry sending messages to the affected contacts. The
            campaign will be reactivated and selected contacts will be queued for retry.
          </Alert>

          {loadingFailedStats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : failedStats.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No failed messages found
            </Typography>
          ) : (
            <Box>
              {failedStats.map((stat, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: selectedErrorGroups.includes(stat.error_reason)
                      ? `2px solid ${colors.primary.main}`
                      : '1px solid #E2E8F0',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#F7FAFC',
                    },
                  }}
                  onClick={() => handleToggleErrorGroup(stat.error_reason)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedErrorGroups.includes(stat.error_reason)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleErrorGroup(stat.error_reason);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: colors.primary.main,
                        '&.Mui-checked': {
                          color: colors.primary.main,
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Error: {stat.error_reason}
                        </Typography>
                        <Chip
                          label={`${stat.count} contact${stat.count > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            backgroundColor: colors.error.light,
                            color: colors.error.dark,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.count} message{stat.count > 1 ? 's' : ''} failed with this error
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setRetryModalOpen(false);
              setSelectedErrorGroups([]);
            }}
          >
            Close
          </Button>
          <Tooltip
            title="This will reactivate the campaign and enroll selected contacts for retry."
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleRetryMessages}
                disabled={retrying || selectedErrorGroups.length === 0}
                sx={{
                  borderRadius: '4px',
                  backgroundColor: colors.primary.main,
                  '&:hover': {
                    backgroundColor: colors.primary.dark,
                  },
                }}
              >
                Retry {contactsToRetryCount} Message
                {contactsToRetryCount !== 1 ? 's' : ''}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
